import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { criarProjeto } from '../../services/projetoService';
import { listarSupervisores } from '../../services/supervisorService';
import { listarUnidadesOrganicas } from '../../services/unidadeOrganicaService';

// Importações Material-UI
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
} from '@mui/material';

function ProjetoForm({ onSuccess, propostaId }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [supervisores, setSupervisores] = useState([]);
    const [unidadesOrganicas, setUnidadesOrganicas] = useState([]);

    const initialFormState = {
        propostaId: propostaId,
        supervisorId: '',
        orgaoId: '',
        observacoes: '',
        documentoProjeto: null,
    };

    const [formData, setFormData] = useState(initialFormState);

    // Carrega supervisores e unidades orgânicas
    useEffect(() => {
        const carregarDados = async () => {
            try {
                const [supervisoresRes, unidadesRes] = await Promise.all([
                    listarSupervisores(),
                    listarUnidadesOrganicas(),
                ]);
                setSupervisores(supervisoresRes.data);
                setUnidadesOrganicas(unidadesRes.data);
            } catch (err) {
                console.error('Erro ao carregar dados:', err);
                setError('Erro ao carregar dados necessários.');
            }
        };
        carregarDados();
    }, []);

    // Atualiza campos de texto/select
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Atualiza campo de arquivo
    const handleFileChange = (e) => {
        const arquivo = e.target.files[0];
        setFormData((prev) => ({
            ...prev,
            documentoProjeto: arquivo,
        }));
    };

    // Limpa o formulário
    const resetForm = () => {
        setFormData(initialFormState);
        setSuccess(false);
        setError(null);
        
        // Resetar o campo de arquivo
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Submete o projeto
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            const formDataObj = new FormData();
            Object.keys(formData).forEach((key) => {
                if (formData[key] !== null) {
                    formDataObj.append(key, formData[key]);
                }
            });

            await criarProjeto(formDataObj);
            setSuccess(true);
            resetForm();
            window.scrollTo({ top: 0, behavior: 'smooth' });
            if (onSuccess) onSuccess();
            
            // Remover mensagem de sucesso após 5 segundos
            setTimeout(() => {
                setSuccess(false);
            }, 5000);
        } catch (err) {
            console.error('Erro ao submeter projeto:', err);
            setError(err.response?.data?.message || 'Erro ao submeter projeto');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setLoading(false);
        }
    };

    // Se ocorrer erro antes de exibir o formulário
    if (error && !loading && supervisores.length === 0 && unidadesOrganicas.length === 0) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box sx={{ mt: 1 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Formulário de Submissão de Projecto
                </Typography>

                {success && (
                    <Alert 
                        severity="success" 
                        sx={{ 
                            mb: 2,
                            p: 2,
                            bgcolor: 'success.light',
                            '& .MuiAlert-icon': {
                                fontSize: '1.5rem'
                            }
                        }}
                    >
                        <Typography variant="subtitle1">
                            Projecto criado com sucesso!
                        </Typography>
                    </Alert>
                )}

                {error && (
                    <Alert 
                        severity="error" 
                        sx={{ 
                            mb: 2,
                            p: 2
                        }}
                    >
                        {error}
                    </Alert>
                )}

                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}
                >
                    {/* Supervisor */}
                    <FormControl fullWidth>
                        <InputLabel id="supervisor-label">Supervisor</InputLabel>
                        <Select
                            labelId="supervisor-label"
                            label="Supervisor"
                            name="supervisorId"
                            value={formData.supervisorId}
                            onChange={handleChange}
                            required
                        >
                            <MenuItem value="">Selecione um supervisor</MenuItem>
                            {supervisores.map((supervisor) => (
                                <MenuItem key={supervisor.id} value={supervisor.id}>
                                    {supervisor.Usuario ? supervisor.Usuario.nome : 'Nome não disponível'} - {supervisor.area_especializacao}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Unidade Orgânica */}
                    <FormControl fullWidth>
                        <InputLabel id="orgao-label">Unidade Orgânica</InputLabel>
                        <Select
                            labelId="orgao-label"
                            label="Unidade Orgânica"
                            name="orgaoId"
                            value={formData.orgaoId}
                            onChange={handleChange}
                            required
                        >
                            <MenuItem value="">Selecione uma unidade orgânica</MenuItem>
                            {unidadesOrganicas.map((unidade) => (
                                <MenuItem key={unidade.id} value={unidade.id}>
                                    {unidade.nome}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Documento do Projeto */}
                    <Box>
                        <Typography variant="subtitle1" gutterBottom>
                            Documento do Projecto
                        </Typography>
                        <TextField
                            type="file"
                            fullWidth
                            inputProps={{ accept: '.pdf,.doc,.docx' }}
                            onChange={handleFileChange}
                            required
                        />
                    </Box>

                    {/* Observações */}
                    <TextField
                        label="Observações"
                        name="observacoes"
                        value={formData.observacoes}
                        onChange={handleChange}
                        multiline
                        rows={4}
                        fullWidth
                    />

                    {/* Botões */}
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button 
                            type="button" 
                            variant="outlined" 
                            onClick={resetForm}
                            disabled={loading}
                        >
                            Limpar Formulário
                        </Button>
                        <Button 
                            type="submit" 
                            variant="contained" 
                            disabled={loading}
                            sx={{
                                position: 'relative',
                                minWidth: 150
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress
                                        size={20}
                                        sx={{ 
                                            color: 'inherit', 
                                            position: 'absolute',
                                            left: '50%',
                                            marginLeft: '-10px'
                                        }}
                                    />
                                    Submetendo...
                                </>
                            ) : (
                                'Submeter Projeto'
                            )}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
}

ProjetoForm.propTypes = {
    onSuccess: PropTypes.func.isRequired,
    propostaId: PropTypes.string.isRequired,
};

export default ProjetoForm;